import React, { useState } from 'react';
import { Input, Select, Button, DatePicker, InputNumber, message, Tooltip } from 'antd';
import { UserAddOutlined, InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';



const { Option } = Select;
const { TextArea } = Input;

const AutomaticDataEntryForClientProspecting = ({ onAddRow, onClose }) => {
  const [formData, setFormData] = useState({
    companyName: '',
    industry: '',
    location: '',
    companySize: null,
    revenue: null,
    current401kProvider: '',
    contactName: '',
    contactTitle: '',
    contactEmail: '',
    contactPhoneNumber: '',
    linkedInProfile: '',
    dateOfFirstContact: null,
    lastContactDate: null,
    contactMethod: '',
    followUpDate: null,
    notesOnConversation: '',
  });

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    if (!formData.companyName || !formData.contactName) {
      message.error('Company Name and Contact Name are required');
      return;
    }

    const newRow = {
      'Company Name': formData.companyName,
      'Industry': formData.industry,
      'Location': formData.location,
      'Company Size': formData.companySize,
      'Revenue': formData.revenue,
      'Current PlanSync AIvider': formData.current401kProvider,
      'Contact Name': formData.contactName,
      'Contact Title': formData.contactTitle,
      'Contact Email': formData.contactEmail,
      'Contact Phone Number': formData.contactPhoneNumber,
      'LinkedIn Profile': formData.linkedInProfile,
      'Date of First Contact': formData.dateOfFirstContact,
      'Last Contact Date': formData.lastContactDate,
      'Contact Method': formData.contactMethod,
      'Follow-Up Date': formData.followUpDate,
      'Notes on Conversation': formData.notesOnConversation,
    };

    onAddRow(newRow);
    message.success('Client prospecting data submitted successfully');
    onClose();
  };

  const renderLabel = (label, tooltip) => (
    <div className="flex items-center">
      <span>{label}</span>
      <Tooltip title={tooltip}>
        <InfoCircleOutlined className="text-gray-400 hover:text-gray-600 ml-2" />
      </Tooltip>
    </div>
  );

  const isFormValid = () => {
    return formData.companyName && formData.contactName;
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-2xl shadow-md">
      <h1 className="text-3xl font-light text-gray-800 mb-6">Client Prospecting Data Entry</h1>
      <form className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Company Name", "Enter the full legal name of the prospective client company.")}
            </label>
            <Input
              placeholder="Company Name"
              value={formData.companyName}
              onChange={(e) => handleChange('companyName', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Industry", "Specify the industry sector the company operates in.")}
            </label>
            <Input
              placeholder="Industry"
              value={formData.industry}
              onChange={(e) => handleChange('industry', e.target.value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Location", "Enter the company's primary business location.")}
            </label>
            <Input
              placeholder="Location"
              value={formData.location}
              onChange={(e) => handleChange('location', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Company Size", "Total number of employees at the company.")}
            </label>
            <InputNumber
              placeholder="Company Size"
              value={formData.companySize}
              onChange={(value) => handleChange('companySize', value)}
              style={{ width: '100%' }}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Revenue", "Annual revenue of the company (if known).")}
            </label>
            <InputNumber
              placeholder="Revenue"
              value={formData.revenue}
              onChange={(value) => handleChange('revenue', value)}
              formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/\$\s?|(,*)/g, '')}
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Current PlanSync AIvider", "Name of their existing retirement plan provider, if any.")}
            </label>
            <Input
              placeholder="Current PlanSync AIvider"
              value={formData.current401kProvider}
              onChange={(e) => handleChange('current401kProvider', e.target.value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Contact Name", "Full name of the primary contact person.")}
            </label>
            <Input
              placeholder="Contact Name"
              value={formData.contactName}
              onChange={(e) => handleChange('contactName', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Contact Title", "Job title or position of the contact person.")}
            </label>
            <Input
              placeholder="Contact Title"
              value={formData.contactTitle}
              onChange={(e) => handleChange('contactTitle', e.target.value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Contact Email", "Business email address of the contact person.")}
            </label>
            <Input
              placeholder="Contact Email"
              value={formData.contactEmail}
              onChange={(e) => handleChange('contactEmail', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Contact Phone Number", "Best phone number to reach the contact person.")}
            </label>
            <Input
              placeholder="Contact Phone Number"
              value={formData.contactPhoneNumber}
              onChange={(e) => handleChange('contactPhoneNumber', e.target.value)}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {renderLabel("LinkedIn Profile", "URL to the contact person's LinkedIn profile.")}
          </label>
          <Input
            placeholder="LinkedIn Profile"
            value={formData.linkedInProfile}
            onChange={(e) => handleChange('linkedInProfile', e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Date of First Contact", "When was the initial contact made with this prospect?")}
            </label>
            <DatePicker
              placeholder="Date of First Contact"
              onChange={(date) => handleChange('dateOfFirstContact', date)}
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Last Contact Date", "Most recent date of communication with the prospect.")}
            </label>
            <DatePicker
              placeholder="Last Contact Date"
              onChange={(date) => handleChange('lastContactDate', date)}
              style={{ width: '100%' }}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Contact Method", "How was the communication conducted?")}
            </label>
            <Select
              placeholder="Contact Method"
              onChange={(value) => handleChange('contactMethod', value)}
              style={{ width: '100%' }}
            >
              <Option value="email">Email</Option>
              <Option value="phone">Phone</Option>
              <Option value="in-person">In-Person</Option>
              <Option value="video-call">Video Call</Option>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Follow-Up Date", "When is the next scheduled contact?")}
            </label>
            <DatePicker
              placeholder="Follow-Up Date"
              onChange={(date) => handleChange('followUpDate', date)}
              style={{ width: '100%' }}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {renderLabel("Notes on Conversation", "Summary of discussions and key points from interactions.")}
          </label>
          <TextArea
            placeholder="Notes on Conversation"
            value={formData.notesOnConversation}
            onChange={(e) => handleChange('notesOnConversation', e.target.value)}
            rows={4}
          />
        </div>

        <div className="flex justify-start">
          <Tooltip title="Save this prospective client's information to the database">
            <Button
              type="primary"
              icon={<UserAddOutlined />}
              onClick={handleSubmit}
              disabled={!isFormValid()}
              size="large"
              className={`inline-flex items-center px-6 py-3 border border-transparent text-lg font-medium rounded-lg shadow-sm text-white ${isFormValid() ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-400 cursor-not-allowed'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150 ease-in-out`}
            >
              Add Prospective Client
            </Button>
          </Tooltip>
        </div>
      </form>
    </div>
  );
};

export default AutomaticDataEntryForClientProspecting;

